/* Las olas de Ona — el logo REAL, no una versión dibujada a mano.
 *
 * Los trazos salen de `web/marca/ona-olas.svg`, que se extrajo del manual de
 * marca en PDF (las curvas del propio archivo, no un calco). El `viewBox` es el
 * original: no se toca, o las proporciones dejan de ser las de la marca.
 *
 * 🔴 Y NO SE USA EL LOGO COMPLETO en el login. Al lado pone «ona pulse», así que
 * el logo con la palabra dentro repite «ona» dos veces, y a 46 px la «o» se lee
 * como un borrón. Criterio de Patricio (25-08): «más ya sería cargarlo mucho».
 */
import React, { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { C, F } from '@/constants/theme';

const OLAS = [
  { d: 'M475.62 -216.19 L475.71 -215.95 C473.95 -215.06 471.36 -213.95 468.23 -213.04 C465.08 -212.14 461.40 -211.45 457.42 -211.45 C450.71 -211.45 445.30 -213.37 441.01 -215.68 C436.71 -217.99 433.55 -220.67 431.32 -222.20 L435.81 -223.85 L440.30 -225.50 C442.56 -223.80 445.93 -221.12 450.06 -218.84 C454.20 -216.56 459.08 -214.72 464.39 -214.72 C466.62 -214.72 468.75 -214.96 470.65 -215.29 C472.55 -215.62 474.23 -216.05 475.52 -216.43 L475.61 -216.19 Z', c: C.aquaDeep },
  { d: 'M486.06 -222.26 C484.89 -222.05 483.50 -221.84 482.09 -221.67 C480.68 -221.51 479.25 -221.40 477.97 -221.40 C470.41 -221.40 463.81 -226.28 456.95 -231.15 C450.09 -236.02 442.98 -240.91 434.40 -240.91 C430.36 -240.91 426.03 -239.98 422.17 -238.80 C418.31 -237.63 414.92 -236.20 412.78 -235.24 L412.83 -234.97 L412.87 -234.70 C414.62 -235.12 417.11 -235.66 419.64 -236.11 C422.18 -236.56 424.79 -236.91 426.78 -236.91 C435.78 -236.91 442.55 -232.29 449.21 -227.69 C455.87 -223.07 462.45 -218.47 471.01 -218.47 C473.35 -218.47 476.18 -218.96 478.90 -219.62 C481.63 -220.28 484.23 -221.10 486.16 -221.76 L486.10 -222.02 L486.04 -222.27 Z', c: C.aqua },
  { d: 'M501.54 -229.10 C500.39 -228.83 499.07 -228.54 497.66 -228.35 C496.26 -228.14 494.76 -228.00 493.28 -228.00 C486.43 -228.00 480.02 -232.84 473.27 -237.70 C466.52 -242.54 459.41 -247.40 451.14 -247.40 C448.03 -247.40 444.49 -246.83 441.23 -246.10 C437.97 -245.36 434.96 -244.46 432.91 -243.79 L432.95 -243.53 L433.00 -243.28 C433.97 -243.41 435.14 -243.53 436.39 -243.64 C437.65 -243.74 439.01 -243.80 440.36 -243.80 C451.26 -243.80 458.64 -239.08 465.41 -234.37 C472.18 -229.65 478.35 -224.94 486.82 -224.94 C489.20 -224.94 491.93 -225.47 494.55 -226.19 C497.17 -226.91 499.70 -227.82 501.66 -228.60 L501.59 -228.84 L501.51 -229.08 Z', c: C.aquaSoft },
];

export function OlasOna({ width = 46 }: { width?: number }) {
  return (
    <Svg width={width} height={width * (35.95 / 88.88)} viewBox="412.78 -247.40 88.88 35.95">
      {OLAS.map((o, i) => <Path key={i} d={o.d} fill={o.c} />)}
    </Svg>
  );
}

/* El latido. Es la única cosa viva de la pantalla, y justifica el nombre del
   producto: un electrocardiograma, no una onda decorativa.
 *
 * 🔴 AHORA SÍ LATE. La versión anterior lo dejaba quieto a propósito, con este
 * argumento: «una animación en el arranque compite con el propio arranque de la
 * app; si se quiere latir, va después y despacio». Patricio lo pidió el 25-08
 * («¿podemos animar el pulse del inicio de sesión?»), así que se hace — y se hace
 * como decía la nota: DESPACIO. La traza se dibuja sola de izquierda a derecha en
 * 2,2 s, espera medio segundo y vuelve a empezar, como un monitor de verdad. No
 * hay rebotes ni parpadeos: la única cosa que se mueve en la pantalla no puede
 * ser la que distrae.
 *
 * Se anima `strokeDashoffset`, que no es una propiedad que pueda ir por el hilo
 * nativo, así que `useNativeDriver: false`. Es una sola línea en una pantalla que
 * no hace nada más, y es justo el caso donde eso no importa.
 *
 * 🔴 Y RESPETA «REDUCIR MOVIMIENTO», que es regla de la casa (docs/05): con la
 * opción activada se pinta la traza entera y quieta, no una versión a medias. */
const TRAZA = 'M0 32 L44 32 L54 32 L62 11 L72 47 L82 26 L94 26 L126 26 L134 32 L140 21 L148 38 L156 32 L240 32';
/* Largo generoso del trazado: si se queda corto, la línea aparece cortada. Pasarse
   solo añade un instante de espera al principio del ciclo, que no se nota. */
const LARGO = 460;
const PathAnimado = Animated.createAnimatedComponent(Path);

export function Latido({ width = 240, duracion = 2200 }: { width?: number; duracion?: number }) {
  const off = useRef(new Animated.Value(LARGO)).current;
  const [quieto, setQuieto] = useState(false);

  useEffect(() => {
    let vivo = true;
    AccessibilityInfo.isReduceMotionEnabled().then((r) => { if (vivo) setQuieto(!!r); });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (r) => setQuieto(!!r));
    return () => { vivo = false; sub?.remove?.(); };
  }, []);

  useEffect(() => {
    if (quieto) { off.setValue(0); return; }
    const ciclo = Animated.loop(Animated.sequence([
      Animated.timing(off, { toValue: 0, duration: duracion, easing: Easing.linear, useNativeDriver: false }),
      Animated.delay(Math.round(duracion * 0.22)),
      Animated.timing(off, { toValue: LARGO, duration: 0, useNativeDriver: false }),
    ]));
    ciclo.start();
    return () => ciclo.stop();
  }, [off, quieto, duracion]);

  return (
    <Svg width="100%" height={width * (52 / 240)} viewBox="0 0 240 52">
      <PathAnimado
        d={TRAZA}
        fill="none" stroke={C.aqua} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray={LARGO} strokeDashoffset={off as unknown as number}
      />
    </Svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CARGANDO — el mismo latido, en pequeño y más rápido.
   Patricio: «a veces algunos datos tardan unos pocos segundos en cargar; en vez
   de poner un "Cargando…", la animación del pulso durante esos 2 o 3 segundos».

   🔴 Y es mejor que un texto por un motivo concreto, no por estética: un
   «Cargando…» quieto no distingue «está viniendo» de «se ha colgado». Una traza
   que se repite dice que algo sigue pasando. Va más rápido que la del login
   (1,1 s en vez de 2,2) porque aquí mide una espera de 2 o 3 segundos: al ritmo
   lento apenas se vería un ciclo y parecería congelado.

   Hereda de `Latido` el respeto por «Reducir movimiento»: con esa opción, traza
   quieta y el texto haciendo el trabajo.
   ══════════════════════════════════════════════════════════════════════════ */
export function Cargando({ texto, alto = 120 }: { texto?: string; alto?: number }) {
  return (
    <View style={{ minHeight: alto, alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10 }}>
      <View style={{ width: 150 }}>
        <Latido width={150} duracion={1100} />
      </View>
      {texto ? (
        <Text style={{ fontFamily: F.ui, fontSize: 11.5, color: C.muted, textAlign: 'center' }}>{texto}</Text>
      ) : null}
    </View>
  );
}
